import { getPathFromSelector } from "@base/util/PathFromSelector";
import { AnyFunction, getKeys } from "./TypeUtil";

const eqSym = Symbol("eq");
const emptySym = Symbol("emptySym");

export class ImmHelper {
  static update<T extends object>(state: T, params: Partial<T>): T {
    if (ImmHelper.isPartialSame(params, state)) return state;
    return {
      ...state,
      ...params,
    };
  }

  static updateOrCreate<T extends object>(state: T | undefined, params: T): T {
    if (!state)
      return params;
    if (ImmHelper.isPartialSame(params, state)) return state;
    return {
      ...state,
      ...params,
    };
  }

  // function t<S extends object, P1 extends keyof S, P2 extends keyof NonNullable<S[P1]>>(o: S, p: P1, p2: P2): NonNullable<S[P1]>[P2] {

  static setProperty<P extends keyof S, S extends object>(state: S, key: P, value: S[P]): S;
  static setProperty<P1 extends keyof S, P2 extends keyof S[P1], S extends object>(state: S, key1: P1, key2: P2, value: S[P1][P2]): S;
  static setProperty<P1 extends keyof S, P2 extends keyof S[P1], P3 extends keyof S[P1][P2], S extends object>(state: S, key1: P1, key2: P2, key3: P3, value: S[P1][P2][P3]): S;
  static setProperty<S extends object>(state: S, ...props: any[]): S {
    switch (props.length) {
      case 2:
        return ImmHelper.setProperty1(state, props[0], props[1]);
      case 3:
        return ImmHelper.setProperty2(state, props[0], props[1], props[2]);
      case 4:
        return ImmHelper.setProperty3(state, props[0], props[1], props[2], props[3]);
      default: {
        return ImmHelper.setPropertyPath(state, props.slice(0, props.length - 1), props[props.length - 1]);
      }
    }
  }

  static setPropertyNonNullable<P extends keyof S, S extends object>(state: S, key: P, value: S[P]): S;
  static setPropertyNonNullable<P1 extends keyof S, P2 extends keyof NonNullable<S[P1]>, S extends object>(state: S, key1: P1, key2: P2, value: NonNullable<S[P1]>[P2]): S;
  static setPropertyNonNullable<P1 extends keyof S, P2 extends keyof NonNullable<S[P1]>, P3 extends keyof NonNullable<NonNullable<S[P1]>[P2]>, S extends object>(state: S, key1: P1, key2: P2, key3: P3, value: NonNullable<NonNullable<S[P1]>[P2]>[P3]): S;
  static setPropertyNonNullable<P1 extends keyof S, P2 extends keyof NonNullable<S[P1]>, P3 extends keyof NonNullable<NonNullable<S[P1]>[P2]>, P4 extends keyof NonNullable<NonNullable<NonNullable<S[P1]>[P2]>[P3]>, S extends object>(state: S, key1: P1, key2: P2, key3: P3, key4: P4, value: NonNullable<NonNullable<NonNullable<S[P1]>[P2]>[P3]>[P4]): S;
  static setPropertyNonNullable<S extends object>(state: S, ...props: any[]): S {
    switch (props.length) {
      case 2:
        return ImmHelper.setProperty1(state, props[0], props[1]);
      case 3:
        return ImmHelper.setProperty2(state, props[0], props[1], props[2]);
      case 4:
        return ImmHelper.setProperty3(state, props[0], props[1], props[2], props[3]);
      default: {
        return ImmHelper.setPropertyPath(state, props.slice(0, props.length - 1), props[props.length - 1]);
      }
    }
  }

  static deleteProperty<P extends keyof S, S extends object>(state: S, key: P): Omit<S, P> {
    const { [key]: _val, ...newState } = state;
    return newState;
  }

  static deleteProperties<P extends keyof S, S extends object>(state: S, keys: P[]): Omit<S, P> {
    const newObject: any = {};
    for (const k of getKeys(state)) {
      if (!keys.includes(k as P)) {
        newObject[k] = state[k];
      }
    }
    return newObject as S;
  }

  static setPropertyWithSelector<S extends object, P>(old: S, selector: (old: S) => P, newValue: P): S {
    if (ImmHelper.compareSelector(old, selector, newValue)) return old;
    const path = getPathFromSelector(selector);
    const currentNew = ImmHelper.setPropertyPath(old, path, newValue, true);
    return currentNew;
  }

  static setPropertyPath<S extends object>(state: S, propertyPath: PropertyKey[], value: any, skipCheck?: boolean): S {
    if (propertyPath.length === 0) return value;

    if (!skipCheck) {
      if (ImmHelper.comparePath(state, propertyPath, value)) return state;
    }

    const stack: any[] = new Array(propertyPath.length);
    stack[0] = state;
    for (let i = 0; i < propertyPath.length - 1; i++) {
      const p = propertyPath[i];
      stack[i + 1] = stack[i][p];
    }

    let last = value;
    for (let i = stack.length - 1; i >= 0; i--) {
      const current = ImmHelper.setProperty1(stack[i], propertyPath[i], last);
      last = current;
    }
    return last;
  }

  static mergePropertyPath<S extends object>(state: S, propertyPath: PropertyKey[], value: any): S {
    if (propertyPath.length === 0) return value;

    const stack: any[] = new Array(propertyPath.length);
    stack[0] = state;
    for (let i = 0; i < propertyPath.length - 1; i++) {
      const p = propertyPath[i];
      stack[i + 1] = stack[i][p];
    }

    let last = value;
    for (let i = stack.length - 1; i >= 0; i--) {
      const current = ImmHelper.mergeProperty1(stack[i], propertyPath[i], last);
      last = current;
    }
    return last;
  }

  static diff(objA: any, objB: any): typeof eqSym | any {
    if (objA === objB) return eqSym;
    if (!objA) return objB;
    if (!objB) return undefined;

    if (Array.isArray(objB)) {
      const diffObject: any[] = [];
      if (Array.isArray(objA)) {
        const m = Math.max(objA.length, objB.length);
        for (let i = 0; i < m; i++) {
          diffObject.push(ImmHelper.diff(objA[i], objB[i]));
        }
      } else {
        console.log("Not handled so far!");
      }
      return diffObject;
    } else {
      const keysA = typeof objA === "string" ? [] : Object.keys(objA);
      const keysB = typeof objB === "string" ? [] : Object.keys(objB);
      const keys = keysA.concat(keysB.filter((x) => keysA.indexOf(x) === -1));

      const diffObject: any = {};
      for (const k of keys) {
        const d = ImmHelper.diff(objA[k], objB[k]);
        if (d !== eqSym) diffObject[k] = d;
      }
      return diffObject;
    }
  }

  public static setProperty1<S, P extends keyof S>(state: S, key: P, value: S[P]): S {
    if (state[key] !== value) {
      if (Array.isArray(state)) {
        const keyNumber = typeof key === "string" ? parseInt(key, 10) : (key as number);
        return ImmHelper.setArrayItem(state, keyNumber, value) as any;
      } else {
        return {
          ...state,
          [key]: value,
        };
      }
    }
    return state;
  }

  private static mergeProperty1<S, P extends keyof S>(state: S, key: P, value: S[P]): S {
    if (Array.isArray(state)) {
      const keyNumber = typeof key === "string" ? parseInt(key, 10) : (key as number);
      return ImmHelper.setArrayItem(state, keyNumber, {
        ...state[keyNumber],
        ...value,
      }) as any;
    } else {
      return {
        ...state,
        [key]: {
          ...state[key],
          ...value,
        },
      };
    }
  }

  private static isPartialSame(a: any, b: any): boolean {
    const keys = getKeys(a);
    for (const k of keys) {
      if (a[k] !== b[k]) return false;
    }
    return true;
  }

  static compareSelector<S, P>(old: S, selector: (old: S) => P, newValue: P): boolean {
    const oldValue = selector(old);
    return oldValue === newValue;
  }

  private static comparePath<S, P>(old: S, propertyPath: PropertyKey[], newValue: P): boolean {
    let current: any = old;
    for (const p of propertyPath) {
      current = current[p];
    }

    return current === newValue;
  }

  static setArrayItem1<S>(old: S[] | undefined, index: number, value: S): S[] {
    if (!old) {
      const a = [];
      a[index] = value;
      return a;
    }
    return [...old.slice(0, index), value, ...old.slice(index + 1)];
  }

  static setArrayItem2<P1 extends keyof S, V, S extends { [x in P1]: V[] }>(old: S, p: P1, index: number, value: V): S {
    return {
      ...old,
      [p]: this.setArrayItem1(old[p], index, value)
    }
  }

  static setArrayItem<S extends any[]>(old: S | undefined, index: number, value: S[0]): S;
  static setArrayItem<P1 extends keyof S, V, S extends { [x in P1]: V[] }>(old: S, p: P1, index: number, value: V): S;
  static setArrayItem<S>(old: any, p1: any, p2: any, p3: any = emptySym): S[] {
    if (p3 === emptySym) return this.setArrayItem1(old, p1, p2);
    return this.setArrayItem2(old, p1, p2, p3)
  }

  static insertArrayItem<S>(old: S[], index: number, value: S): S[] {
    return [...old.slice(0, index), value, ...old.slice(index)];
  }

  static pushArrayItem1<S>(old: S[], value: S): S[] {
    return [...old, value];
  }

  static pushArrayItem2<P1 extends keyof S, V, S extends { [x in P1]: V[] }>(old: S, p: P1, value: V): S {
    return {
      ...old,
      [p]: this.pushArrayItem1(old[p], value),
    };
  }

  static pushArrayItem<P1 extends keyof S, V, S extends { [x in P1]: V[] }>(old: S, p: P1, value: V): S;
  static pushArrayItem<S>(old: S[], value: S): S[];
  static pushArrayItem(old: any, p1: any, p2: any = emptySym): any {
    if (p2 === emptySym) return this.pushArrayItem1(old, p1);
    return this.pushArrayItem2(old, p1, p2);
  }

  static removeArrayItem1<S>(old: S[], index: number): S[] {
    const newArray = old.slice();
    newArray.splice(index, 1);
    return newArray;
  }

  static removeArrayItem2<P1 extends keyof S, S extends { [x in P1]: any[] }>(old: S, p: P1, index: number): S {
    return {
      ...old,
      [p]: this.removeArrayItem1(old[p], index),
    };
  }

  static removeArrayItem<S>(old: S[], index: number): S[];
  static removeArrayItem<P1 extends keyof S, S extends { [x in P1]: any[] }>(old: S, p: P1, index: number): S;
  static removeArrayItem(old: any, p1: any, p2: any = emptySym): any {
    if (p2 !== emptySym)
      return this.removeArrayItem2(old, p1, p2);
    return this.removeArrayItem1(old, p1);
  }

  static moveArrayItem<S>(old: S[], fromIndex: number, toIndex: number) {
    return old.map((_p, i) => {
      let index = i;
      if (i === toIndex)
        index = fromIndex;
      else {
        if (i >= fromIndex && i < toIndex)
          index++;
        else if (i <= fromIndex && i > toIndex)
          index--;
      }
      return old[index];
    });
  }

  static concat<T>(a: T[], b: T[]): T[] {
    if (a && b && a.length > 0 && b.length > 0) return [...a, ...b];
    if (b && b.length > 0) return b;
    return a;
  }

  public static setProperty2<P1 extends keyof S, P2 extends keyof S[P1], S extends object>(state: S, key1: P1, key2: P2, value: S[P1][P2]): S {
    const s1 = state[key1];
    if (s1 === undefined) {
      throw new Error("setProperty2 - invalid path!");
    }

    return ImmHelper.setProperty1(state, key1, ImmHelper.setProperty1(s1, key2, value));
  }

  public static setProperty3<P1 extends keyof S, P2 extends keyof S[P1], P3 extends keyof S[P1][P2], S extends object>(state: S, key1: P1, key2: P2, key3: P3, value: S[P1][P2][P3]): S {
    const s1 = state[key1];
    if (!s1) {
      throw new Error("setProperty3a - invalid path!");
    }
    const s2 = s1[key2];
    if (!s2) {
      throw new Error("setProperty3b - invalid path!");
    }

    return ImmHelper.setProperty1(state, key1, ImmHelper.setProperty1(s1, key2, ImmHelper.setProperty1(s2, key3, value)));
  }

  static getPathFromSelector = getPathFromSelector;
}

export type Mutable<T> = {
  -readonly [P in keyof T]: Mutable<T[P]>;
};

export type InferMut<T> = T extends Imm<infer M> ? M : never;

export function asMutNew<T>(param: Imm<T>): Mut<T> {
  return param as Mut<T>;
}

export const asMut = asMutNew;

export function asMutOld<T>(param: Imm<T>): Mut<T> {
  return param as Mut<T>;
}

export function asMut3<T>(param: T): Mut<T> {
  return param as Mut<T>;
}

export function asMut2<T>(param: Imm<T>): Mutable<T> {
  return param as Mutable<T>;
}

// IMMER
export type Mut1<T> = { -readonly [K in keyof T]: Mut1<T[K]> };
export type Mut2<T> = T extends Imm<infer T2> ? T2 : never;
export type Mut<T> = Mut3<T>;

/** Convert a mutable type into a readonly type */
// export type Imm<T> = { readonly [K in keyof T]: T[K] extends never ? T[K] : Imm<T[K]> };
export type Imm2<T> = { readonly [K in keyof T]: Imm2<T[K]> };

type ImmutablePrimitive = undefined | null | boolean | string | number | AnyFunction;

export type Immutable<T> = T extends ImmutablePrimitive
  ? T
  :
  T extends Array<infer U>
  ? ImmutableArray<U>
  : T extends Map<infer K, infer V>
  ? ImmutableMap<K, V>
  : T extends Set<infer M>
  ? ImmutableSet<M>
  : ImmutableObject<T>;

export type ImmutableArray<T> = ReadonlyArray<Immutable<T>>;
export type ImmutableMap<K, V> = ReadonlyMap<Immutable<K>, Immutable<V>>;
export type ImmutableSet<T> = ReadonlySet<Immutable<T>>;
export type ImmutableObject<T> = { readonly [K in keyof T]: Immutable<T[K]> };
export type Imm<T> = Immutable<T>;

type Mut3<T> = T extends ImmutablePrimitive
  ? T
  : T extends ImmutableArray<infer U>
  ? U[]
  : T extends ImmutableMap<infer K, infer V>
  ? Map<K, V>
  : T extends ImmutableSet<infer M>
  ? Set<M>
  : T extends ImmutableObject<infer O>
  ? O
  : undefined;